<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import appDocumentService, { type AppDocument } from '../services/app-document.service'
import LanguageSelector from '../components/LanguageSelector.vue'

const { t, locale } = useI18n()
const route = useRoute()

const document = ref<AppDocument | null>(null)
const loading = ref(false)
const error = ref(false)

const localeMap: Record<string, string> = {
  pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE',
  it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE',
  da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT',
  hr: 'hr-HR', fi: 'fi-FI'
}

const fetchDocument = async () => {
  loading.value = true
  error.value = false
  document.value = null
  try {
    const response = await appDocumentService.getDocument('payment_terms', locale.value)
    document.value = response.document
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const urlLang = route.query.lang as string
  const supportedLangs = Object.keys(localeMap)
  if (urlLang && supportedLangs.includes(urlLang)) {
    locale.value = urlLang
    localStorage.setItem('language', urlLang)
  }
  fetchDocument()
})

watch(locale, fetchDocument)
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Top bar -->
    <div class="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div class="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        <div class="flex items-center gap-2 text-gray-600">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <span class="text-sm font-medium text-gray-700">{{ t('appDocuments.paymentTermsTitle') }}</span>
        </div>
        <LanguageSelector />
      </div>
    </div>

    <div class="max-w-4xl mx-auto px-4 py-8">
      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-32">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p class="text-gray-500 text-sm">{{ t('appDocuments.loading') }}</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex flex-col items-center justify-center py-32">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <p class="text-gray-700 font-medium mb-4">{{ t('appDocuments.error') }}</p>
        <button
          class="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          @click="fetchDocument"
        >
          {{ t('appDocuments.retry') }}
        </button>
      </div>

      <!-- Content -->
      <template v-else-if="document">
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <!-- Document header -->
          <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-10">
            <div class="flex items-center gap-3 mb-2">
              <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <span class="text-blue-100 text-sm font-medium uppercase tracking-wider">{{ t('appDocuments.version') }} {{ document.version }}</span>
            </div>
            <h1 class="text-3xl font-bold text-white">{{ t('appDocuments.paymentTermsTitle') }}</h1>
            <p class="text-blue-100 text-sm mt-2">
              {{ t('appDocuments.lastUpdated') }}
              {{ new Date(document.updated_at).toLocaleDateString(localeMap[locale] || 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }}
            </p>
          </div>

          <!-- Document body -->
          <div class="px-8 py-10">
            <div class="doc-body" v-html="document.content"></div>
          </div>

          <!-- Document footer -->
          <div class="bg-gray-50 border-t border-gray-200 px-8 py-4">
            <p class="text-xs text-gray-500 text-center">
              {{ t('appDocuments.lastUpdated') }}
              {{ new Date(document.updated_at).toLocaleDateString(localeMap[locale] || 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }}
              &nbsp;·&nbsp; {{ t('appDocuments.version') }} {{ document.version }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

